import {DataStatus, Flag, useFormContext} from 'platform/components';
import {HStack, Text, VStack, Show} from 'platform/foundation';

import {memo, useCallback, useMemo} from 'react';
import {Controller} from 'react-hook-form';

import i18n from '@dms/i18n';
import {FeaturePickerItem, FeaturesPicker, FeatureSubCategory} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useApiEnums} from '../../../hooks/useApiEnums';
import {useFeatureLabels} from '../../../hooks/useFeatureLabels';
import {collapseSelectedFeatureValues} from '../../../utils/collapseSelectedFeatureValues';
import {expandSelectedFeatureValues} from '../../../utils/expandSelectedFeatureValues';
import {getFeaturesPresetTags} from '../utils/getFeaturesPresetTags';
import {PresetEditorFormValues} from '../utils/presetEditorFormSchema';
import {SeparatorWithLabel} from './SeparatorWithLabel';

interface PresetFeaturesFieldProps extends TestIdProps {
  isReadOnly?: boolean;
}

export function PresetFeaturesField(props: PresetFeaturesFieldProps) {
  const {isReadOnly} = props;
  const {control} = useFormContext<PresetEditorFormValues>();
  const {getEnum, featureSubCategories, isLoading, isError} = useApiEnums();

  // useMemo for correctness: stable references required by MemoizedPresetFeaturesPicker.
  const featureCategories = useMemo(() => getEnum('feature_category'), [getEnum]);
  const features = useMemo(() => getEnum('feature'), [getEnum]);

  return (
    <VStack spacing={2}>
      <SeparatorWithLabel label={i18n.t('entity.vehicle.labels.features')} />

      <Controller
        name="features"
        control={control}
        render={({field}) => (
          <VStack spacing={2}>
            <PresetFeaturesSummary
              value={field.value}
              data-testid={suffixTestId('features', props)}
            />

            <Show when={!isReadOnly}>
              <DataStatus isLoading={isLoading} isError={isError}>
                <MemoizedPresetFeaturesPicker
                  categories={featureCategories}
                  features={features}
                  subCategories={featureSubCategories}
                  value={field.value}
                  onChange={field.onChange}
                  data-testid={suffixTestId('features', props)}
                />
              </DataStatus>
            </Show>
          </VStack>
        )}
      />
    </VStack>
  );
}

interface PresetFeaturesSummaryProps extends TestIdProps {
  value: string[] | null;
}

/** Shows the selection while the picker is collapsed. Only content rendered for system presets. */
function PresetFeaturesSummary(props: PresetFeaturesSummaryProps) {
  const {getFeatureLabel, isKnownFeature} = useFeatureLabels();
  const labels = getFeaturesPresetTags(props.value, getFeatureLabel, isKnownFeature);

  if (labels.length === 0) {
    return (
      <Text size="xSmall" color="secondary" data-testid={suffixTestId('summaryEmpty', props)}>
        {i18n.t('entity.sourcing.relativeFilters.summary.noConstraints')}
      </Text>
    );
  }

  return (
    <HStack spacing={1} wrap>
      {labels.map((label, index) => (
        <Flag
          key={`${index}-${label}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
          label={label}
          size="small"
          colorScheme="blue"
          isSubtle
          data-testid={suffixTestId(`summary-[${index}]`, props)}
        />
      ))}
    </HStack>
  );
}

interface PresetFeaturesPickerProps extends TestIdProps {
  categories: FeaturePickerItem[];
  features: FeaturePickerItem[];
  subCategories: FeatureSubCategory[] | undefined;
  value: string[] | null;
  onChange: (value: string[] | null) => void;
}

/** Memoized as in `AllOffers/…/FilterFeatures.tsx`; `memo` needs referentially stable props. */
const MemoizedPresetFeaturesPicker = memo((props: PresetFeaturesPickerProps) => {
  const {categories, features, subCategories, value, onChange} = props;

  // useMemo for correctness: stable reference required by FeaturesPicker.
  const selected = useMemo(
    () => expandSelectedFeatureValues(value ?? [], features),
    [features, value]
  );

  const handleFeatureSelect = useCallback(
    (nextSelected: string[]) => {
      const collapsed = collapseSelectedFeatureValues(nextSelected, features);
      onChange(collapsed.length > 0 ? collapsed : null);
    },
    [features, onChange]
  );

  return (
    <FeaturesPicker
      categories={categories}
      features={features}
      subCategories={subCategories}
      selected={selected}
      onFeatureSelect={handleFeatureSelect}
      allowSubItemMultiselect
      isExpandable
      data-testid={suffixTestId('picker', props)}
    />
  );
});
